// src/components/UserPredictions2026.js
// Display user's 2026 season predictions

import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../utils/apiClient';

const UserPredictions2026 = () => {
  const { user } = useContext(AuthContext);
  const [predictions, setPredictions] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedRaceId, setExpandedRaceId] = useState(null);

  useEffect(() => {
    const fetchPredictions = async () => {
      try {
        setLoading(true);
        setError('');
        
        const response = await apiClient.get('/api/predictions/user');
        
        // Filter for 2026 season only (season field defaults to 2025, so only include those with season: 2026)
        const predictions2026 = response.data.filter(p => p.season === '2026' || p.season === 2026);
        setPredictions(predictions2026);
      } catch (err) {
        console.error('Error fetching predictions:', err);
        setError('Failed to load your predictions');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchPredictions();
    }
  }, [user]);

  // Fetch drivers for 2026 season
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        const response = await apiClient.get('/api/f1data/drivers?season=2026');
        setDrivers(response.data);
      } catch (err) {
        console.error('Error fetching drivers:', err);
      }
    };

    fetchDrivers();
  }, []);

  const formatDate = (dateString) => {
    const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

 const getDriverCode = (driverId) => {
    if (!driverId) return 'Not selected';
    
    // Look up by driverId first, then _id as fallback
    const driver = drivers.find(d => d.driverId === driverId || d._id === driverId);
    if (driver) {
      return driver.code || driverId;
    }
    
    // Fallback: if already a short code return uppercase
    if (driverId.length <= 4) return driverId.toUpperCase();
    return driverId.substring(0, 3).toUpperCase();
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return <div className="text-center py-4">Loading your predictions...</div>;
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  if (predictions.length === 0) {
    return (
      <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
        <p className="font-bold">No predictions yet</p>
        <p>Make your first 2026 season prediction using the form on the left!</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-xl font-bold mb-4 text-gray-800">Your 2026 Predictions</h2>
      
      <div className="space-y-3">
        {predictions.map((prediction) => {
          // For 2026, use featurePredictions and sprintPredictions
          const featurePreds = prediction.featurePredictions || {};
          const sprintPreds = prediction.sprintPredictions || {};
          
          // Determine if this race has sprint predictions
          const hasSprint = prediction.race?.isSprint && Object.keys(sprintPreds).length > 0;
          
          return (
            <div key={prediction._id} className="border rounded-lg p-4 bg-gray-50">
              <div
                className="flex justify-between items-center cursor-pointer"
                onClick={() => setExpandedRaceId(expandedRaceId === prediction._id ? null : prediction._id)}
              >
                <div>
                  <p className="font-semibold text-gray-800">
                    {prediction.race?.raceName || 'Race'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {prediction.race?.date ? formatDate(prediction.race.date) : 'Date unknown'}
                  </p>
                </div>
                <span className="text-gray-600">
                  {expandedRaceId === prediction._id ? '▲' : '▼'}
                </span>
              </div>

              {expandedRaceId === prediction._id && (
                <div className="mt-4 pt-4 border-t space-y-4 text-sm text-gray-700">
                  {/* FEATURE PREDICTIONS */}
                  <div>
                    <p className="font-bold text-gray-800 mb-3">🏁 FEATURE RACE PREDICTIONS</p>
                    
                    {/* Top 5 */}
                    {(featurePreds.podium?.[0] || featurePreds.podium?.[1] || featurePreds.podium?.[2] || featurePreds.podium?.[3] || featurePreds.podium?.[4]) && (
                      <div className="ml-2">
                        <p className="font-semibold text-gray-800 mb-2">Top 5:</p>
                        <div className="ml-4 space-y-1">
                          {featurePreds.podium?.[0] && (
                            <p>🥇 P1: {getDriverCode(featurePreds.podium[0])}</p>
                          )}
                          {featurePreds.podium?.[1] && (
                            <p>🥈 P2: {getDriverCode(featurePreds.podium[1])}</p>
                          )}
                          {featurePreds.podium?.[2] && (
                            <p>🥉 P3: {getDriverCode(featurePreds.podium[2])}</p>
                          )}
                          {featurePreds.podium?.[3] && (
                            <p>🏅 P4: {getDriverCode(featurePreds.podium[3])}</p>
                          )}
                          {featurePreds.podium?.[4] && (
                            <p>🎖️ P5: {getDriverCode(featurePreds.podium[4])}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Other Predictions */}
                    <div className="ml-2 mt-2">
                      <p className="font-semibold text-gray-800 mb-2">Other Predictions:</p>
                      <div className="ml-4 space-y-1">
                        {featurePreds.polePosition && (
                          <p>⚡ Pole: {getDriverCode(featurePreds.polePosition)}</p>
                        )}
                        {featurePreds.fastestLap && (
                          <p>🏁 Fastest Lap: {getDriverCode(featurePreds.fastestLap)}</p>
                        )}
                        {featurePreds.firstRetirement && (
                          <p>🚩 First Retirement: {getDriverCode(featurePreds.firstRetirement)}</p>
                        )}
                        {featurePreds.mostPlacesGained && (
                          <p>🚀 Most Places Gained: {getDriverCode(featurePreds.mostPlacesGained)}</p>
                        )}
                      </div>
                    </div>

                    {/* Feature Head-to-Heads */}
                    {(featurePreds.driverHeadToHead?.winner || featurePreds.teamHeadToHead?.winner) && (
                      <div className="ml-2 mt-2">
                        <p className="font-semibold text-gray-800 mb-2">Head-to-Head:</p>
                        <div className="ml-4 space-y-1">
                          {featurePreds.driverHeadToHead?.winner && (
                            <p>👥 Driver H2H: {getDriverCode(featurePreds.driverHeadToHead.winner)}</p>
                          )}
                          {featurePreds.teamHeadToHead?.winner && (
                            <p>🏢 Team H2H: {getDriverCode(featurePreds.teamHeadToHead.winner)}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* SPRINT PREDICTIONS (only if sprint weekend) */}
                  {hasSprint && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="font-bold text-gray-800 mb-3">👟 SPRINT RACE PREDICTIONS</p>
                      
                      {/* Top 5 */}
                      {(sprintPreds.podium?.[0] || sprintPreds.podium?.[1] || sprintPreds.podium?.[2] || sprintPreds.podium?.[3] || sprintPreds.podium?.[4]) && (
                        <div className="ml-2">
                          <p className="font-semibold text-gray-800 mb-2">Top 5:</p>
                          <div className="ml-4 space-y-1">
                            {sprintPreds.podium?.[0] && (
                              <p>🥇 P1: {getDriverCode(sprintPreds.podium[0])}</p>
                            )}
                            {sprintPreds.podium?.[1] && (
                              <p>🥈 P2: {getDriverCode(sprintPreds.podium[1])}</p>
                            )}
                            {sprintPreds.podium?.[2] && (
                              <p>🥉 P3: {getDriverCode(sprintPreds.podium[2])}</p>
                            )}
                            {sprintPreds.podium?.[3] && (
                              <p>🏅 P4: {getDriverCode(sprintPreds.podium[3])}</p>
                            )}
                            {sprintPreds.podium?.[4] && (
                              <p>🎖️ P5: {getDriverCode(sprintPreds.podium[4])}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Sprint Other Predictions */}
                      <div className="ml-2 mt-2">
                        <p className="font-semibold text-gray-800 mb-2">Other Predictions:</p>
                        <div className="ml-4 space-y-1">
                          {sprintPreds.polePosition && (
                            <p>⚡ Pole: {getDriverCode(sprintPreds.polePosition)}</p>
                          )}
                          {sprintPreds.fastestLap && (
                            <p>🏁 Fastest Lap: {getDriverCode(sprintPreds.fastestLap)}</p>
                          )}
                          {sprintPreds.firstRetirement && (
                            <p>🚩 First Retirement: {getDriverCode(sprintPreds.firstRetirement)}</p>
                          )}
                          {sprintPreds.mostPlacesGained && (
                            <p>🚀 Most Places Gained: {getDriverCode(sprintPreds.mostPlacesGained)}</p>
                          )}
                        </div>
                      </div>

                      {/* Sprint Head-to-Heads */}
                      {(sprintPreds.driverHeadToHead?.winner || sprintPreds.teamHeadToHead?.winner) && (
                        <div className="ml-2 mt-2">
                          <p className="font-semibold text-gray-800 mb-2">Head-to-Head:</p>
                          <div className="ml-4 space-y-1">
                            {sprintPreds.driverHeadToHead?.winner && (
                              <p>👥 Driver H2H: {getDriverCode(sprintPreds.driverHeadToHead.winner)}</p>
                            )}
                            {sprintPreds.teamHeadToHead?.winner && (
                              <p>🏢 Team H2H: {getDriverCode(sprintPreds.teamHeadToHead.winner)}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <p className="text-xs text-gray-500 pt-2">
                    Submitted: {formatDate(prediction.createdAt)}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserPredictions2026;